/**
 * ConsentCheckbox — the required T&C + Privacy consent tick for a signup card.
 *
 * Extracted verbatim from RegisterPage (2026-07-16) when the page split into
 * per-method signup cards. Each card owns its OWN checked state: the tick is
 * bound to the button it enables, which is the whole point of the split — a
 * single shared tick previously gated both the email submit and the Google
 * redirect from inside the email field stack.
 *
 * `id` is REQUIRED and has no default on purpose. This renders twice on one
 * page and the Label's htmlFor binds to it — a shared default would give the
 * Google card's label control of the email card's checkbox.
 *
 * US-002 / AC-1 / AC-2 / AC-3 / FR-G-001 / NFR-PDPA-001: one required,
 * affirmative, unticked-by-default checkbox covering BOTH the Terms of Service
 * and the Privacy Policy. Both links open the CANONICAL documents on the
 * marketing site in a new tab (no embedded copy — single source of truth), so
 * the in-progress form is not lost.
 *
 * Consent is SERVER-AUTHORITATIVE (BUG-20260630-114135): the client asserts a
 * boolean only. There is deliberately no version prop — do not add one.
 */

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TERMS_URL, PRIVACY_URL } from '@/constants/tnc';

export interface ConsentCheckboxProps {
  /** Unique per rendered instance — e.g. 'tnc' | 'googleTnc'. */
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function ConsentCheckbox({ id, checked, onCheckedChange, disabled }: ConsentCheckboxProps) {
  return (
    <div className="flex items-start gap-3 min-h-[44px]">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="mt-0.5 shrink-0"
      />
      <Label htmlFor={id} className="text-sm text-otter-earth-800 leading-snug cursor-pointer">
        I confirm I am the parent/guardian and I agree to the{' '}
        {/* stopPropagation is load-bearing: without it, clicking a link inside
            the Label also toggles the checkbox. */}
        <a
          href={TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-otter-earth-700 underline hover:text-otter-orange-500"
          onClick={(e) => e.stopPropagation()}
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-otter-earth-700 underline hover:text-otter-orange-500"
          onClick={(e) => e.stopPropagation()}
        >
          Privacy Policy
        </a>
      </Label>
    </div>
  );
}
